import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';

/**
 * lazy() com auto-cura: se o chunk falhar (deploy trocou os hashes / cache
 * velho), recarrega a página UMA vez para buscar o index.html novo em vez de
 * mostrar "Importing a module script failed".
 */
function lazyRetry(fn: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    fn().catch((err) => {
      const KEY = 'chunk-reload-at';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 15_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        // Nunca resolve — a página está recarregando.
        return new Promise<never>(() => {});
      }
      throw err;
    }),
  );
}

// Funcionario / publico
const Landing = lazyRetry(() => import('@/pages/Landing'));
const Login = lazyRetry(() => import('@/pages/Login'));
const BaterPonto = lazyRetry(() => import('@/pages/BaterPonto'));
const Historico = lazyRetry(() => import('@/pages/Historico'));
const Biometria = lazyRetry(() => import('@/pages/Biometria'));
const NotFound = lazyRetry(() => import('@/pages/NotFound'));

// Admin
const AdminLayout = lazyRetry(() => import('@/components/admin/AdminLayout'));
const AdminDashboard = lazyRetry(() => import('@/pages/Admin/Dashboard'));
const AdminFuncionarios = lazyRetry(() => import('@/pages/Admin/Funcionarios'));
const AdminFuncionarioForm = lazyRetry(() => import('@/pages/Admin/FuncionarioForm'));
const AdminFuncionarioBiometria = lazyRetry(() => import('@/pages/Admin/FuncionarioBiometria'));
const AdminFuncionarioImport = lazyRetry(() => import('@/pages/Admin/FuncionarioImport'));
const AdminDispositivos = lazyRetry(() => import('@/pages/Admin/Dispositivos'));
const AdminEscalas = lazyRetry(() => import('@/pages/Admin/Escalas'));
const AdminGeofences = lazyRetry(() => import('@/pages/Admin/Geofences'));
const AdminGeofenceForm = lazyRetry(() => import('@/pages/Admin/GeofenceForm'));
const AdminRegistros = lazyRetry(() => import('@/pages/Admin/Registros'));
const AdminRelatorios = lazyRetry(() => import('@/pages/Admin/Relatorios'));
const AdminAuditoria = lazyRetry(() => import('@/pages/Admin/Auditoria'));
const AdminEmpresas = lazyRetry(() => import('@/pages/Admin/Empresas'));
const AdminSecretarias = lazyRetry(() => import('@/pages/Admin/Secretarias'));
const AdminUsuarios = lazyRetry(() => import('@/pages/Admin/Usuarios'));
const AdminConfiguracoes = lazyRetry(() => import('@/pages/Admin/Configuracoes'));
const AdminNotificacoes = lazyRetry(() => import('@/pages/Admin/Notificacoes'));

function Loading() {
  return (
    <div className="d-flex h-screen-safe align-items-center justify-content-center text-muted">
      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
      Carregando...
    </div>
  );
}

function SuspenseOutlet() {
  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
}

// Landing pública em '/' — usuário logado é redirecionado dentro da própria Landing.

export const router = createBrowserRouter([
  {
    element: <SuspenseOutlet />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/login', element: <Login /> },
      {
        element: (
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        ),
        children: [
          { path: '/bater-ponto', element: <BaterPonto /> },
          { path: '/historico', element: <Historico /> },
          { path: '/biometria', element: <Biometria /> },
        ],
      },
      {
        element: (
          <ProtectedRoute kind="USER">
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: '/admin', element: <AdminDashboard /> },
          { path: '/admin/funcionarios', element: <AdminFuncionarios /> },
          { path: '/admin/funcionarios/novo', element: <AdminFuncionarioForm /> },
          { path: '/admin/funcionarios/import', element: <AdminFuncionarioImport /> },
          { path: '/admin/funcionarios/:id/editar', element: <AdminFuncionarioForm /> },
          { path: '/admin/funcionarios/:id/biometria', element: <AdminFuncionarioBiometria /> },
          { path: '/admin/dispositivos', element: <AdminDispositivos /> },
          { path: '/admin/escalas', element: <AdminEscalas /> },
          { path: '/admin/geofences', element: <AdminGeofences /> },
          { path: '/admin/geofences/novo', element: <AdminGeofenceForm /> },
          { path: '/admin/geofences/:id/editar', element: <AdminGeofenceForm /> },
          { path: '/admin/registros', element: <AdminRegistros /> },
          { path: '/admin/relatorios', element: <AdminRelatorios /> },
          { path: '/admin/auditoria', element: <AdminAuditoria /> },
          { path: '/admin/empresas', element: <AdminEmpresas /> },
          { path: '/admin/secretarias', element: <AdminSecretarias /> },
          { path: '/admin/usuarios', element: <AdminUsuarios /> },
          { path: '/admin/configuracoes', element: <AdminConfiguracoes /> },
          { path: '/admin/notificacoes', element: <AdminNotificacoes /> },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export default router;
